// Layout compartido para emails transaccionales del cliente (React).
// Mantiene el mismo look & feel que el helper de edge functions
// (supabase/functions/_shared/emailLayout.ts) para que los usuarios reciban
// una imagen visual consistente sin importar quién dispara el envío.

const LOGO_URL = 'https://crm.ceroideas.es/logo-ceroideas.png';
const CRM_URL = 'https://crm.ceroideas.es';
const CONTACT_URL = 'https://ceroideas.es';

const ACCENTS = {
  primary: { border: '#2563eb', bg: '#eff6ff', text: '#1e40af' },
  success: { border: '#059669', bg: '#ecfdf5', text: '#065f46' },
  warning: { border: '#d97706', bg: '#fffbeb', text: '#92400e' },
  danger:  { border: '#dc2626', bg: '#fef2f2', text: '#991b1b' },
} as const;

export type EmailAccent = keyof typeof ACCENTS;

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string
));

export interface EmailLayoutInput {
  preheader?: string;
  title: string;
  accent?: EmailAccent;
  body: string;                     // HTML libre del cuerpo
  cta?: { label: string; url: string };
  footerNote?: string;
}

export const renderEmailLayout = ({
  preheader = '',
  title,
  accent = 'primary',
  body,
  cta,
  footerNote,
}: EmailLayoutInput): string => {
  const accentColors = ACCENTS[accent];
  const ctaHtml = cta ? `
    <tr>
      <td align="center" style="padding: 8px 32px 24px;">
        <a href="${cta.url}"
           style="display: inline-block; padding: 14px 32px; background-color: #2563eb; color: #ffffff !important; text-decoration: none; border-radius: 8px; font-weight: 600; font-size: 15px;">
          ${esc(cta.label)}
        </a>
      </td>
    </tr>
    <tr>
      <td style="padding: 0 32px 12px;" align="center">
        <p style="font-size: 12px; color: #9ca3af; margin: 0 0 4px 0;">Si el botón no funciona, copia este enlace:</p>
        <p style="font-size: 12px; color: #2563eb; margin: 0; word-break: break-all;">${esc(cta.url)}</p>
      </td>
    </tr>
  ` : '';

  return `
<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${esc(title)}</title>
</head>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; background-color: #f3f4f6; color: #1f2937;">
  ${preheader ? `<div style="display: none; max-height: 0; overflow: hidden;">${esc(preheader)}</div>` : ''}

  <table role="presentation" cellpadding="0" cellspacing="0" width="100%" style="background-color: #f3f4f6; padding: 32px 12px;">
    <tr>
      <td align="center">
        <table role="presentation" cellpadding="0" cellspacing="0" width="600" style="max-width: 600px; background-color: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,0.06);">
          <tr>
            <td style="padding: 28px 32px 20px; border-bottom: 1px solid #e5e7eb;">
              <img src="${LOGO_URL}" alt="Cero Ideas" width="140" style="display: block; max-width: 140px; height: auto;">
            </td>
          </tr>

          <tr>
            <td style="padding: 8px 32px 0; border-left: 3px solid ${accentColors.border};">
              <h1 style="font-size: 22px; font-weight: 700; color: #111827; margin: 24px 0 8px; line-height: 1.3;">
                ${esc(title)}
              </h1>
            </td>
          </tr>

          <tr>
            <td style="padding: 8px 32px 20px; font-size: 15px; color: #374151; line-height: 1.6;">
              ${body}
            </td>
          </tr>

          ${ctaHtml}

          <tr>
            <td style="padding: 20px 32px 28px; background-color: #f9fafb; border-top: 1px solid #e5e7eb; text-align: center;">
              <p style="margin: 0 0 6px 0; font-size: 12px; color: #6b7280;">
                © ${new Date().getFullYear()} Cero Ideas ·
                <a href="${CRM_URL}" style="color: #2563eb; text-decoration: none;">CRM</a> ·
                <a href="${CONTACT_URL}" style="color: #2563eb; text-decoration: none;">ceroideas.es</a>
              </p>
              ${footerNote ? `<p style="margin: 0; font-size: 11px; color: #9ca3af;">${esc(footerNote)}</p>` : ''}
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>
  `.trim();
};

export const chip = (label: string, color: 'blue' | 'green' | 'amber' | 'red' | 'gray' = 'blue'): string => {
  const palette = {
    blue:  'background-color: #eff6ff; color: #1e40af;',
    green: 'background-color: #ecfdf5; color: #065f46;',
    amber: 'background-color: #fffbeb; color: #92400e;',
    red:   'background-color: #fef2f2; color: #991b1b;',
    gray:  'background-color: #f3f4f6; color: #374151;',
  }[color];
  return `<span style="display: inline-block; padding: 3px 10px; border-radius: 999px; font-size: 13px; font-weight: 600; ${palette}">${esc(label)}</span>`;
};

export const renderEmailText = ({
  title,
  bodyText,
  cta,
  footerNote,
}: {
  title: string;
  bodyText: string;
  cta?: { label: string; url: string };
  footerNote?: string;
}): string => {
  return [
    title,
    '',
    bodyText,
    cta ? '' : null,
    cta ? `${cta.label}: ${cta.url}` : null,
    '',
    footerNote || null,
    '',
    '— Cero Ideas · https://ceroideas.es',
  ].filter((l) => l !== null).join('\n');
};

// Helper para las tablas de "summary" que aparecen en varios emails.
export const summaryTable = (rows: Array<[string, string]>): string => `
  <table role="presentation" cellpadding="0" cellspacing="0" width="100%" style="border-collapse: collapse; margin: 12px 0 4px;">
    ${rows.map(([label, value]) => `
      <tr>
        <td style="padding: 8px 0; color: #6b7280; font-size: 13px; width: 140px;">${label}</td>
        <td style="padding: 8px 0; color: #111827; font-size: 14px;">${value}</td>
      </tr>
    `).join('')}
  </table>
`;
